import React, { useState } from "react";
import { Button, Input, Modal, message } from "antd";
import { useMutation } from "@apollo/client";
import {
  APPROVE_ORDER_MODERATION,
  REJECT_ORDER_MODERATION,
} from "../../api/graphql/moderation";
import CustomSelect from "../CustomSelect";
import dialog from "../../styles/dialog.module.scss";
import styles from "./styles.module.scss";

interface ModerationActionsProps {
  orderType: string;
  hashId: number;
  /** Already approved: it can still be canceled until it is sent. */
  approved?: boolean;
  onDone: () => void;
}

type Decision = "WAITING" | "APPROVED" | "REJECT";

/**
 * Approve or cancel an order in moderation from one design-system select.
 * Reject asks for the reason first: the order is refunded in full and the
 * client is told it.
 */
const ModerationActions: React.FC<ModerationActionsProps> = ({
  orderType,
  hashId,
  approved,
  onDone,
}) => {
  const [approve, { loading: approving }] = useMutation(
    APPROVE_ORDER_MODERATION,
  );
  const [reject, { loading: rejecting }] = useMutation(REJECT_ORDER_MODERATION);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState("");

  const handleApprove = async () => {
    try {
      await approve({ variables: { orderType, hashId } });
      message.success(`Order #${hashId} approved, it goes to the contractor`);
      onDone();
    } catch (error: any) {
      message.error(error?.message || "The order was not approved");
    }
  };

  const handleReject = async () => {
    try {
      await reject({
        variables: { orderType, hashId, reason: reason.trim() || null },
      });
      message.success(`Order #${hashId} canceled, the client is refunded`);
      setRejectOpen(false);
      setReason("");
      onDone();
    } catch (error: any) {
      message.error(error?.message || "The order was not canceled");
    }
  };

  // The select shows where the order stands; picking Reject only opens the
  // dialog, so closing it leaves the select as it was.
  const current: Decision = approved ? "APPROVED" : "WAITING";
  const options: Array<{ value: Decision; label: string }> = approved
    ? [
        { value: "APPROVED", label: "Approved" },
        { value: "REJECT", label: "Reject" },
      ]
    : [
        { value: "WAITING", label: "Waiting" },
        { value: "APPROVED", label: "Approve" },
        { value: "REJECT", label: "Reject" },
      ];

  const handleDecision = (decision: Decision) => {
    if (decision === "APPROVED" && !approved) handleApprove();
    if (decision === "REJECT") setRejectOpen(true);
  };

  return (
    <span className={styles.actions}>
      <CustomSelect
        className={styles.decision}
        aria-label={`Moderation of order #${hashId}`}
        value={current}
        options={options}
        disabled={approving || rejecting}
        onChange={(value) => handleDecision(value as Decision)}
      />

      <Modal
        open={rejectOpen}
        title={<span className={dialog.title}>Reject order #{hashId}</span>}
        onCancel={() => setRejectOpen(false)}
        width={480}
        footer={[
          <div key="footer" className={dialog.footer}>
            <Button
              className={dialog.cancel}
              onClick={() => setRejectOpen(false)}
            >
              Back
            </Button>
            <Button
              className={dialog.delete}
              type="primary"
              loading={rejecting}
              onClick={handleReject}
            >
              Reject and refund
            </Button>
          </div>,
        ]}
      >
        <p className={styles.modalText}>
          The order is canceled and its full price goes back to the client’s
          balance. The client sees the reason.
        </p>
        <label className={dialog.label} htmlFor={`reject-${hashId}`}>
          Reason
        </label>
        <Input.TextArea
          id={`reject-${hashId}`}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. The app breaks the store rules"
          autoSize={{ minRows: 3, maxRows: 6 }}
          maxLength={500}
        />
      </Modal>
    </span>
  );
};

export default ModerationActions;
